import {
  AppRoute,
  QueryReference,
  type PageAdminHomeData,
} from '@agentselly/immodossier-shared';
import { WrapperWidget } from '@agentselly/ui';
import { createFileRoute } from '@tanstack/react-router';
import { useBackendQuery } from '~/helper/useBackendQuery';
import { useTranslate } from '@tolgee/react';
import { clsx } from 'clsx';
import { LoadingWidget } from '~/components/widget/base/LoadingWidget';
import { ErrorWidget } from '~/components/widget/base/ErrorWidget';
import { AppLink } from '~/components/AppLink';
import { useAppParams } from '~/helper/useAppParams';

export const Route = createFileRoute(AppRoute.$LANG_$COMPANYID_ADMIN_HOME)({
  component: Body,
});

function Body() {
  const { t } = useTranslate();
  const { lang, companyId } = useAppParams();

  const query = useBackendQuery(QueryReference.PageAdminHome);

  if (query.isLoading) {
    return <LoadingWidget />;
  }

  if (query.isError) {
    return <ErrorWidget error={query.error} />;
  }

  const data = query.data || ({} as PageAdminHomeData);

  if (!data.v3Redesign) {
    return (
      <WrapperWidget>
        <p>{t('admin_home_content')}</p>
        <br />
        {Object.entries((query.data as Record<string, any>) || {}).map(
          (
            [
              key,
              value,
            ],
            index,
          ) => (
            <div
              key={index}
              className={clsx(
                'overflow-hidden py-2',
                index !== 0
                  ? 'border-gray-100 dark:border-gray-700 border-t-2'
                  : '',
              )}
            >
              <div>
                <span>{key}</span>
                <pre className="px-2 bg-primary-100 float-end rounded">
                  {JSON.stringify(value).replace(/"(.*?)"/, '$1')}
                </pre>
              </div>
            </div>
          ),
        )}
      </WrapperWidget>
    );
  }

  return (
    <WrapperWidget>
      <div className="v3-stack">
        {/* Header */}
        <div className="v3-head">
          <h1>{t('admin_home_title', 'Administration')}</h1>
          <p>
            {t('admin_home_subtitle', 'Verwaltung, Status und Systemübersicht')}
          </p>
        </div>

        {/* Live KPI Cards */}
        <div className="v3-kpi-grid">
          <div className="v3-kpi-card">
            <span className="n">
              {data.evaluationsCount !== undefined
                ? data.evaluationsCount.toLocaleString('de-CH')
                : '–'}
            </span>
            <span className="l">
              {t('admin_evaluations_title', 'Bewertungen')}
            </span>
            <span className="d">
              {t(
                'admin_evaluations_desc',
                'Gesamtzahl aller erfassten Immobilienbewertungen im System',
              )}
            </span>
            <div className="mt-2">
              <AppLink
                to={AppRoute.$LANG_$COMPANYID_ADMIN_EVALUATIONS}
                params={{ lang, companyId }}
                className="v3-btn sm"
              >
                {t('admin_view_evaluations', 'Bewertungen ansehen')} →
              </AppLink>
            </div>
          </div>

          <div className="v3-kpi-card">
            <span className="n">
              {data.usersCount !== undefined
                ? data.usersCount.toLocaleString('de-CH')
                : '–'}
            </span>
            <span className="l">{t('admin_users_title', 'Benutzer')}</span>
            <span className="d">
              {t(
                'admin_users_desc',
                'Registrierte Benutzerkonten und Zugriffsrollen in Clerk',
              )}
            </span>
            <div className="mt-2">
              <AppLink
                to={AppRoute.$LANG_$COMPANYID_ADMIN_USERS}
                params={{ lang, companyId }}
                className="v3-btn sm"
              >
                {t('admin_view_users', 'Benutzer verwalten')} →
              </AppLink>
            </div>
          </div>
        </div>

        {/* Account Details Card */}
        <div className="v3-card">
          <h2 className="text-base font-bold text-[var(--v3-tx)]">
            {t('admin_account_title', 'Aktueller Account & Berechtigungen')}
          </h2>
          <dl className="v3-kv">
            <div>
              <dt>{t('admin_account_role', 'Rolle')}</dt>
              <dd>
                <span className="v3-chip c-pri">{data.role || 'Guest'}</span>
              </dd>
            </div>
            <div>
              <dt>{t('admin_account_status', 'Status')}</dt>
              <dd>
                <span
                  className={clsx(
                    'v3-chip',
                    data.isAuthenticated ? 'c-ok' : 'c-warn',
                  )}
                >
                  {data.isAuthenticated
                    ? t('admin_account_authenticated', 'Angemeldet')
                    : t('admin_account_guest', 'Nicht angemeldet')}
                </span>
              </dd>
            </div>
            <div>
              <dt>{t('admin_account_hubspot_id', 'HubSpot Contact ID')}</dt>
              <dd className="font-mono text-xs">
                {data.hubspotContactId || '–'}
              </dd>
            </div>
            <div>
              <dt>{t('admin_account_clerk_id', 'Clerk User ID')}</dt>
              <dd className="font-mono text-xs">{data.clerkUserId || '–'}</dd>
            </div>
          </dl>
        </div>

        {/* Technical parameters details accordion */}
        <details className="v3-acc">
          <summary>
            <span>
              {t('admin_tech_params', 'Technische Parameter & Rohdaten')}
            </span>
            <span className="v3-chev transition-transform">▼</span>
          </summary>
          <div className="v3-acc-in">
            <p className="text-xs text-[var(--v3-mu)] mb-3">
              {t(
                'admin_tech_params_desc',
                'Rohdaten der aktuellen Admin-Sitzung und Kontextparameter.',
              )}
            </p>
            <pre className="v3-pre">
              {JSON.stringify(
                {
                  params: data.params,
                  hubspotContactId: data.hubspotContactId,
                  role: data.role,
                  isAuthenticated: data.isAuthenticated,
                  clerkUserId: data.clerkUserId,
                  evaluationsCount: data.evaluationsCount,
                  usersCount: data.usersCount,
                },
                null,
                2,
              )}
            </pre>
          </div>
        </details>
      </div>
    </WrapperWidget>
  );
}
